import { twMerge } from '@sortlist-frontend/design-system';
import { Button, Rating } from '@sortlist-frontend/design-system/ssr';
import { ImageSourceHelper } from '@sortlist-frontend/media';
import { ClaimedFlag, FeaturedFlag, getFormattedRating, TrustedFlag } from '@sortlist-frontend/shared-components';
import { useTrack } from '@sortlist-frontend/tracking';
import { TrackingParamsCpc } from '@sortlist-frontend/tracking/legacy';
import { TFunction } from '@sortlist-frontend/translation/server-only';
import { useIsVisible } from '@sortlist-frontend/utils';
import Image from 'next/image';
import { MouseEvent, MutableRefObject, useEffect, useRef, useState } from 'react';

import { DomainInfo } from '_backend/integration/domain-info';
import { Obfuscate } from '_components/common/Obfuscate/Obfuscate';
import { NavigationData } from '_components/layout/Layout';
import { getUrlHelper } from '_components/layout/PrimaryNav/DesktopNav/utils';
import { Expertise } from '_core/repos/expertises.repo';
import { useTrackWithoutContext } from '_core/repos/track.repo';
import { Agency, Page, PagesAgency } from '_types/public-api';

import {
  AgencyCompareCta,
  Badges,
  BudgetDisplay,
  LanguagesDisplay,
  LocationDisplay,
  SectorsDisplay,
  TeamSizeDisplay,
  WorksDisplay,
} from './components';
import { useDirectBriefingData } from './hooks/useDirectBriefingData';
import { useLogoAnimation } from './hooks/useLogoAnimation';
import { buildRedirectUrlParams, useTrackAgencyCardClick } from './hooks/useTrackAgencyCardClick';

export type AgencyCardProps = {
  page: Page;
  t: TFunction;
  agency: Agency;
  data: PagesAgency;
  /** This is the position relative to other cards in the section */
  relativePosition: number;
  /** This is the absolute position in the page regardless of the section */
  absolutePosition: number;
  paid?: boolean;
  navigationData: NavigationData;
  appRouter?: boolean;
  expertises?: Expertise[];
  onCardClick?: (slug: string) => void;
};

/* eslint-disable sonarjs/cognitive-complexity */
export const AgencyCard = (props: AgencyCardProps) => {
  const {
    data,
    paid,
    page,
    t,
    navigationData,
    appRouter = false,
    expertises,
    relativePosition,
    absolutePosition,
    onCardClick,
  } = props;
  const agency = props?.agency?.attributes;
  const { origin, locale } = navigationData;
  const domainInfo = DomainInfo.getFromOrigin(origin);
  const getUrl = getUrlHelper(domainInfo, locale);

  const [isDisabled, setIsDisabled] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { trackAgencyProfile } = useTrackAgencyCardClick({
    agency: props.agency,
    data,
    navigationData,
    paid,
    relativePosition,
    absolutePosition,
  });

  const { setDirectBriefingDataKey } = useDirectBriefingData({ page });

  const serviceName = page?.data?.attributes?.metadata?.service_name;
  const pageExpertiseId = page?.data?.attributes?.briefing_options?.expertise?.id;

  const addresses = [...new Set(agency?.addresses.map(({ address }) => address[locale]))];
  const sectors = [...new Set(agency?.sectors.map(({ name }) => name[locale]))];

  const hasReviews = agency != null && agency.reviews_count > 0;

  const rating = hasReviews ? getFormattedRating(agency.reviews_rating_total / agency.reviews_count) : 0;

  const agencyCardRef = useRef<HTMLDivElement>(null);
  const hadBeenInViewport = useRef(false);
  const { track } = useTrack({ app: 'public', locale });
  const { mutateAsync: internalTrack } = useTrackWithoutContext(origin);

  const trackAgencyImpression = ({
    pagesAgency,
    agency,
    relativePosition,
    absolutePosition,
  }: {
    pagesAgency: PagesAgency;
    agency: Agency['attributes'];
    relativePosition: number;
    absolutePosition: number;
  }) => {
    track({
      slug: 'agencyImpression',
      data: {
        agencyUuid: agency?.external_id,
        agencySlug: agency?.slug,
        agencyName: agency?.name,
        agencyPlan: agency?.visibility_plan,
        position: paid === true ? relativePosition : pagesAgency?.attributes.position + 1,
        absolutePosition,
        cardPosition: relativePosition,
        result_type: pagesAgency?.attributes?.section?.toLowerCase(),
        resultType: pagesAgency?.attributes?.section?.toLowerCase(),
        type: 'longtails',
        pageType: 'longtail',
        pageSection: paid ? 'paid' : 'organic',
        pageId: pagesAgency?.relationships?.page?.data?.id,
      },
    });
  };

  const isCardVisible = useIsVisible({
    ref: agencyCardRef as unknown as MutableRefObject<Element>,
    onIsVisible: () => {
      if (hadBeenInViewport.current) return;
      hadBeenInViewport.current = true;
      const trackData: TrackingParamsCpc = {
        name: 'agencyImpression',
        url: location.href,
        agency_external_id: agency.external_id,
        position: paid ? relativePosition : data?.attributes.position + 1,
        origin: 'originLongtail',
        result_type: data?.attributes.section,
      };
      internalTrack(trackData);
      trackAgencyImpression({
        pagesAgency: data,
        agency,
        relativePosition,
        absolutePosition,
      });
    },
    rootMargin: '-100px', // track when we see first 100px of the card
  });

  const isLogoAnimated = useLogoAnimation({ isCardVisible });

  const handleDisableCardOnClick = () => {
    setIsDisabled(true);

    if (timeoutRef.current != null) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setIsDisabled(false);
    }, 2000);
  };

  const handleCardClicked = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    e.stopPropagation();
    if (isDisabled) {
      e.preventDefault();
      return;
    }
    onCardClick?.(agency.slug);
    handleDisableCardOnClick();
    trackAgencyProfile({ internal: true, external: true });
    setDirectBriefingDataKey();
  };

  const handleWebsiteClick = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    e.stopPropagation();
    e.preventDefault();
    onCardClick?.(agency.slug);
    handleDisableCardOnClick();
    setDirectBriefingDataKey();
    // If it's paid, we don't want to track clicks internally, instead they are handled in the /redirect route
    trackAgencyProfile({ internal: false, external: true, openWebsite: true });
    const params = buildRedirectUrlParams({
      agency: props.agency,
      paid,
      position: relativePosition,
      data,
      navigationData,
    });
    window.open(`/agency/${agency.slug}/redirect?${params}`, '_blank');
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current != null) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <li className="list-reset">
      <Obfuscate
        href={getUrl('agency.profile', { slug: agency.slug })}
        target={'_blank'}
        onClick={handleCardClicked}
        onMouseDown={(e) => {
          if (e.button !== 1) return;
          handleCardClicked(e);
        }}
        id={`agency-card-${agency.slug}-${paid ? 'featured' : 'normal'}`}
        className={twMerge(
          'no-underline text-neutral-900 agency-card-content border border-secondary-300 bg-neutral-50 rounded-xl shadow-2 cursor-pointer height-100 layout-column layout-align-space-between-center relative',
          isDisabled && '!cursor-not-allowed',
        )}>
        <div ref={agencyCardRef} className="agency-info layout-column layout-align-start-center flex-1">
          <Image
            suppressHydrationWarning
            className="agency-logo rounded-md shadow-2 relative outline-none mt-6"
            src={ImageSourceHelper.fromUrl(agency?.logo ?? '/_img/default/default-image-square.svg', {
              width: 150,
              quality: 75,
              staticFrame: !isLogoAnimated,
            })}
            width={80}
            height={80}
            alt={agency.name}
            unoptimized
          />
          <div className="agency-name layout-column layout-align-center-center py-4">
            <div className="layout-row layout-align-start-center mt-2 gap-x-2">
              <p title={agency.name} className="line-clamp-2 bold flex-noshrink text-center h5">
                <span className="text-secondary-900 layout-row gap-x-2">{agency.name}</span>
              </p>
              <span className="layout-row layout-align-start-center flags gap-x-1">
                {agency.claimed === true && <ClaimedFlag className="flag" isPrimary={true} />}
                {agency.trusted_partner === true && <TrustedFlag className="flag" isPrimary={true} />}
                {paid === true && <FeaturedFlag className="flag" isPrimary={true} />}
              </span>
            </div>

            <div className="agency-rating mt-2 layout-row layout-align-center-center ">
              {hasReviews ? <span className="bold h6 mr-1">{Math.trunc(rating * 10) / 10}</span> : null}
              <Rating direction={'row'} rate={rating} size={16} className="flex-none">
                {agency.reviews_count === 0 ? (
                  <span className="ml-2 text-secondary-500 small text-truncate" suppressHydrationWarning>
                    ({t('longtail:agencyCard.reviewsCount_zero', { count: agency.reviews_count })})
                  </span>
                ) : (
                  <span className="ml-2 text-secondary-500 small text-truncate" suppressHydrationWarning>
                    ({t('longtail:agencyCard.reviewsCount', { count: agency.reviews_count })})
                  </span>
                )}
              </Rating>
            </div>
            {agency.tagline != null ? (
              <p
                className="small medium text-center my-2 text-break-word"
                dangerouslySetInnerHTML={{ __html: agency.tagline }}></p>
            ) : null}
          </div>
        </div>

        <Badges
          agency={agency}
          t={t}
          data={data}
          expertises={expertises}
          paid={!!paid}
          pageExpertiseId={pageExpertiseId}
        />
        <div className="agency-main agency-main-info layout-align-space-between-start bg-secondary-50 rounded-bottom-xl border-top border-secondary-300 width-100 gap-y-4 gap-x-4">
          <WorksDisplay
            worksCount={data.attributes.works_count}
            serviceName={serviceName}
            t={t}
            appRouter={appRouter}
          />
          <LocationDisplay
            locationNames={addresses}
            metadata={page.data.attributes.metadata}
            t={t}
            appRouter={appRouter}
          />
          <BudgetDisplay
            budget={data.attributes.budget}
            serviceName={serviceName}
            t={t}
            appRouter={appRouter}
            locale={locale}
          />
          <SectorsDisplay sectors={sectors} t={t} appRouter={appRouter} />
          <LanguagesDisplay
            languages={agency.languages}
            t={t}
            appRouter={appRouter}
            locale={locale}
            mainLocale={agency.locale}
          />
          <TeamSizeDisplay teamSize={agency.team_size} t={t} appRouter={appRouter} />
          <div className="layout-row layout-align-start-center width-100 agency-card-cta">
            <AgencyCompareCta
              agency={{
                logo: agency.logo,
                name: agency.name,
                slug: agency.slug,
                external_id: agency.external_id,
              }}
              paid={!!paid}
              locale={locale}
            />
            {paid && !!agency.website_url ? (
              <Button
                onClick={(e) => {
                  handleWebsiteClick(e);
                }}
                onMouseDown={(e) => {
                  if (e.button !== 1) return;
                  handleWebsiteClick(e);
                }}
                disabled={isDisabled}
                id={`agency-card-cta-${agency.slug}-featured`}
                className="flex-100 text-center"
                buttonStyle="primary"
                buttonVariant="raised"
                size="md"
                label={t('longtail:featured.visitWebsite')}
              />
            ) : (
              <Button
                id={`agency-card-cta-${agency.slug}-normal`}
                className="flex-100 text-center"
                buttonStyle="primary"
                buttonVariant="light"
                size="md"
                label={t('longtail:agencyCard.buttons.profile')}
                disabled={isDisabled}
              />
            )}
          </div>
        </div>
      </Obfuscate>
    </li>
  );
};
